/**
 * browser-client
 *
 * Provides the shared browser Supabase client.
 *
 * Primary responsibilities:
 * - Keep one Supabase client instance per browser context
 * - Configure hosted desktop cookies and auth refresh locking
 *
 * Dependencies:
 * - @supabase/ssr
 * - lib/auth/browser-auth-lock
 *
 * Safari note:
 * auth-js ≤2.71 defaults to navigatorLock when `auth.lock` is unset, and
 * Safari throws DOMException `UnavailableError` from navigator.locks during
 * refresh. Lock options are resolved at client-create time (not module load)
 * so SSR evaluation never freezes `lock: undefined` into the browser client.
 */

import { createBrowserClient } from '@supabase/ssr'
import { processLock, type SupabaseClient } from '@supabase/supabase-js'

import { getBrowserAuthLock } from '@/lib/auth/browser-auth-lock'
import { isLoopbackHostname } from '@/lib/auth/desktop-loopback-cookies'
import { isDesktopHost } from '@/lib/desktop-host'
import { Database } from '@/supabase/types'

function isDesktopHostedBrowser() {
  return isDesktopHost()
}

function getDesktopHostedCookie(name: string) {
  if (typeof document === 'undefined') {
    return undefined
  }

  const encodedName = `${name}=`
  const cookieEntry = document.cookie
    .split(';')
    .map((part) => part.trim())
    .find((part) => part.startsWith(encodedName))

  if (!cookieEntry) {
    return undefined
  }

  return decodeURIComponent(cookieEntry.slice(encodedName.length))
}

function setDesktopHostedCookie(
  name: string,
  value: string,
  options?: {
    path?: string
    domain?: string
    sameSite?: string
    secure?: boolean
    httpOnly?: boolean
    maxAge?: number
    expires?: string | number | Date
  }
) {
  if (typeof document === 'undefined') {
    return
  }

  const cookieParts = [`${name}=${encodeURIComponent(value)}`]
  cookieParts.push(`path=${options?.path || '/'}`)

  if (options?.domain) {
    cookieParts.push(`domain=${options.domain}`)
  }

  if (typeof options?.maxAge === 'number') {
    cookieParts.push(`max-age=${options.maxAge}`)
  }

  if (options?.expires) {
    const expires =
      options.expires instanceof Date ? options.expires : new Date(options.expires)
    cookieParts.push(`expires=${expires.toUTCString()}`)
  }

  if (options?.sameSite) {
    cookieParts.push(`samesite=${options.sameSite}`)
  }

  const shouldSetSecure =
    options?.secure &&
    window.location.protocol === 'https:' &&
    !isLoopbackHostname(window.location.hostname)

  if (shouldSetSecure) {
    cookieParts.push('secure')
  }

  document.cookie = cookieParts.join(';')
}

function resolveBrowserAuthLock() {
  // Prefer the localStorage-backed lock; fall back to in-process processLock
  // so auth-js never falls through to navigatorLock on Safari.
  return getBrowserAuthLock() ?? processLock
}

function buildBrowserClientOptions() {
  const authLock = resolveBrowserAuthLock()

  // @supabase/ssr 0.0.10 does:
  //   let cookies = {}; ({ cookies, ... } = options)
  // If `options` is passed without `cookies`, that destructure sets
  // cookies=undefined and later `cookies.get` throws during login/session
  // load ("Cannot read properties of undefined (reading 'get')").
  // Always pass an explicit cookies object.
  const defaultCookies = {}

  if (isDesktopHostedBrowser()) {
    return {
      // Avoid @supabase/ssr's module singleton caching a lock-less client
      // created by another import path before this factory runs.
      isSingleton: false as const,
      auth: {
        lock: authLock,
        autoRefreshToken: false,
        // The native desktop session remains the source of truth, but the hosted
        // browser still needs the mirrored Supabase auth cookie for same-origin
        // API routes like /api/chat/tools-stream and /api/extensions.
        persistSession: true,
        detectSessionInUrl: false
      },
      cookies: {
        get: (name: string) => getDesktopHostedCookie(name),
        set: (name: string, value: string, options?: Parameters<typeof setDesktopHostedCookie>[2]) =>
          setDesktopHostedCookie(name, value, options),
        remove: (name: string, options?: Parameters<typeof setDesktopHostedCookie>[2]) =>
          setDesktopHostedCookie(name, '', {
            ...options,
            maxAge: 0
          })
      }
    }
  }

  return {
    isSingleton: false as const,
    cookies: defaultCookies,
    auth: {
      lock: authLock
    }
  }
}

let cachedSupabase: SupabaseClient<Database> | null = null

export function getBrowserSupabaseClient(): SupabaseClient<Database> {
  if (!cachedSupabase) {
    cachedSupabase = createBrowserClient<Database>(
      process.env.NEXT_PUBLIC_SUPABASE_URL!,
      process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY!,
      buildBrowserClientOptions()
    ) as unknown as SupabaseClient<Database>
  }

  return cachedSupabase
}

export const supabase = new Proxy({} as SupabaseClient<Database>, {
  get(_target, prop) {
    if (prop === 'then') {
      return undefined
    }

    const client = getBrowserSupabaseClient()
    const value = Reflect.get(client as object, prop, client)

    return typeof value === 'function' ? value.bind(client) : value
  },
  set(_target, prop, value) {
    const client = getBrowserSupabaseClient()
    return Reflect.set(client as object, prop, value, client)
  },
  has(_target, prop) {
    return prop in getBrowserSupabaseClient()
  },
  ownKeys() {
    return Reflect.ownKeys(getBrowserSupabaseClient() as object)
  },
  getOwnPropertyDescriptor(_target, prop) {
    return Object.getOwnPropertyDescriptor(getBrowserSupabaseClient() as object, prop)
  }
}) as unknown as SupabaseClient<Database>

export const createClient = () => getBrowserSupabaseClient()
